/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { AutoSizer, Table, Column } from 'react-virtualized';

import FlexBox from './FlexBox';
import ScrollbarRenderer from './ScrollbarRenderer';

const DEFAULT_WIDTH = 245;
const FALLBACK_HEIGHT = 340;
const DIV_STYLE = { height: '100%', flex: '1 1 auto' };
const NO_GRID_STYLE = {
  '-ms-overflow-style': 'none', // IE 10+
};
const NO_GRID_SCROLL_STYLE = {
  ...NO_GRID_STYLE,
  overflowY: 'hidden',
};
const SCROLLABLE_STYLE = { pointerEvents: 'auto' };
const DEFAULT_HEADER_STYLE = {
  'color': '#999',
  'font-weight': 'normal',
  'lineHeight': '1',
  'text-transform': 'initial',
};

const Container = styled(FlexBox)`
  flex: 0 1 auto;
  overflow: ${({ contentOverflowVisible }) => contentOverflowVisible ? 'visible' : 'hidden'}; /* visible dropdown when not logged in */
  position: relative;
  width: 100%;
  /* push the scrollbar off-screen in firefox for windows */
  @supports (-moz-appearance: none) {
    min-height: 100%;
    overflow-x: hidden;
  }
`;

export const TableWithoutScrollBars = styled(Table)`
  .ReactVirtualized__Grid {
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  /* push the scrollbar off-screen in firefox for windows */
  @supports (-moz-appearance: none) {
    .ReactVirtualized__Grid {
      width: ${({ width }) => width + 15}px !important;
    }
  }
  .ReactVirtualized__Table__headerRow {
    background: ${({ theme, isHeaderBgWhite }) => isHeaderBgWhite ? '' : theme.boxheadbg};
    ${({ theme, isHeaderBgWhite }) => isHeaderBgWhite ? '' : `border-bottom: 1px solid ${theme.border};`}
  }
  .ReactVirtualized__Table__headerColumn {
    display: flex;
    align-items: center;
    height: 100%;
    ${({ theme, isHeaderBgWhite }) => isHeaderBgWhite ? '' : `border-right: 1px solid ${theme.border};`}
  }
  .ReactVirtualized__Table__headerColumn:last-of-type {
    border-right: none;
  }
  .ReactVirtualized__Table__rowColumn {
    display: flex;
    align-items: center;
  }
`;

const mapStateToProps = ({ isLoggedIn }) => ({ isLoggedIn });

type Props = {
  isLoggedIn?: boolean,
  ref?: React.Element<*>,
  scrollable?: boolean,
  rowCount: number,
  realRowCount?: number,
  rowHeight: number | Function,
  headerHeight?: number,
  nonScrollableScrollIndex?: number,
  defaultWidth?: number,
  defaultHeight?: number,
  children: number | string | React.Element<*> | Array<any>,
  onRowClick?: Function,
  onRowMouseOver?: Function,
  onRowMouseOut?: Function,
  getScrollTop?: Function,
  isHeaderBgWhite?: boolean,
  contentOverflowVisible: boolean,
  isBottom?: Function,
};

type State = {
  clientHeight?: number,
  scrollHeight?: number,
  scrollTop?: number,
};

@connect(mapStateToProps)
class ScrollableTable extends React.PureComponent<Props, State> {
  static Column = Column;

  _divRef = React.createRef();

  _lastScrollTop: number;

  _lastClientHeight: number;

  _tableRef: React.Element<*>;

  static defaultProps = {
    isLoggedIn: false,
    ref: undefined,
    scrollable: true,
    realRowCount: 0,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: FALLBACK_HEIGHT,
    nonScrollableScrollIndex: 0,
    headerHeight: 0,
    onRowClick: () => {},
    onRowMouseOver: () => {},
    onRowMouseOut: () => {},
    getScrollTop: () => {},
    isHeaderBgWhite: false,
    contentOverflowVisible: false,
    isBottom: () => {},
  }

  constructor(props: Props) {
    super(props);
    const {
      defaultHeight = 0, headerHeight = 0, rowCount, rowHeight,
    } = this.props;
    this.state = {
      clientHeight: (defaultHeight - headerHeight) || FALLBACK_HEIGHT,
      scrollHeight: rowHeight ? rowCount * (typeof rowHeight === 'function' ? rowHeight() : rowHeight) : FALLBACK_HEIGHT,
      scrollTop: 0,
    };
    if (props.ref) this._tableRef = props.ref;
  }

  componentDidMount() {
    this._update();
  }

  componentDidUpdate() {
    this._update();
  }

  static getDerivedStateFromProps = ({ rowHeight, rowCount }: Props) => ({
    scrollHeight: (typeof rowHeight === 'function' ? rowHeight() : rowHeight) * rowCount,
  });

  _update = () => {
    const { scrollTop } = this.state;
    const { headerHeight } = this.props;
    let clientHeight = this._divRef.current && this._divRef.current.clientHeight;
    if (clientHeight && headerHeight) clientHeight -= headerHeight;
    if (!clientHeight ||
      this._lastClientHeight === clientHeight ||
      this._lastScrollTop === scrollTop) return;
    clientHeight && (this._lastClientHeight = clientHeight);
    scrollTop && (this._lastScrollTop = scrollTop);
    this._onScroll({ clientHeight, scrollTop });
  }

  _onScroll = ({ clientHeight, scrollTop } : { clientHeight: number, scrollTop?: number}) => {
    const {
      rowCount, rowHeight, getScrollTop, isBottom,
    } = this.props;
    getScrollTop && getScrollTop(scrollTop);
    const scrollHeight = rowHeight ? rowCount * (typeof rowHeight === 'function' ? rowHeight() : rowHeight) : FALLBACK_HEIGHT;
    // sometimes clientHeight is not provided to onScroll, for now we ignore it in this case
    if (clientHeight) this.setState({ clientHeight, scrollHeight, scrollTop });
    else this.setState({ scrollHeight, scrollTop });
    if ((clientHeight + scrollTop) === scrollHeight && isBottom) {
      isBottom(true);
    }
  }

  _isScrollable = () => {
    const { scrollable, realRowCount, rowHeight } = this.props;
    if (!scrollable) return false;
    const { clientHeight } = this.state;
    if (!clientHeight || !realRowCount) return scrollable;
    return realRowCount * (typeof rowHeight === 'function' ? rowHeight() : rowHeight) > clientHeight;
  }

  render() {
    const {
      defaultWidth,
      defaultHeight,
      headerHeight,
      rowCount,
      nonScrollableScrollIndex,
      onRowClick,
      onRowMouseOver,
      onRowMouseOut,
      isHeaderBgWhite,
      contentOverflowVisible,
    } = this.props;
    const {
      clientHeight,
      scrollHeight,
      scrollTop,
    } = this.state;
    const scrollable = this._isScrollable();
    return (
      <Container contentOverflowVisible={contentOverflowVisible} flex="1 1 100%" direction="row" wrap="nowrap">
        <div ref={this._divRef} style={DIV_STYLE}>
          {/* recommended to have this div */}
          <AutoSizer
            defaultWidth={defaultWidth}
            defaultHeight={defaultHeight}
            nonce={typeof window !== 'undefined' && window.__NONCE__}
          >
            {({ width, height }) => (
              <TableWithoutScrollBars
                width={width}
                height={height}
                onScroll={this._onScroll}
                headerStyle={DEFAULT_HEADER_STYLE}
                rowStyle={SCROLLABLE_STYLE}
                gridStyle={!scrollable ? NO_GRID_SCROLL_STYLE : NO_GRID_STYLE}
                scrollToIndex={!scrollable ? nonScrollableScrollIndex : undefined}
                isHeaderBgWhite={isHeaderBgWhite}
                {...this.props}
                onRowClick={onRowClick}
                onRowMouseOver={onRowMouseOver}
                onRowMouseOut={onRowMouseOut}
                rowClassName={({ index }) => index === -1 ? '' : 'notranslate'}
              />
            )}
          </AutoSizer>
        </div>
        {scrollable ? (
          <ScrollbarRenderer
            headerHeight={headerHeight}
            clientHeight={clientHeight}
            scrollHeight={scrollHeight}
            scrollTop={scrollTop}
            // update scrollbar when items count or scrollability changes
            rowCount={rowCount}
            scrollable={scrollable}
            onRowClick={onRowClick}
            onRowMouseOver={onRowMouseOver}
            onRowMouseOut={onRowMouseOut}
          />) : null}
      </Container>
    );
  }
}

export default ScrollableTable;
